import { GlButton, GlSkeletonLoader } from '@gitlab/ui';
import { mapState, mapActions } from 'pinia';
import { mergeUrlParams } from '~/lib/utils/url_utility';
import SafeHtml from '~/vue_shared/directives/safe_html';
import DiffFileHeader from '~/diffs/components/diff_file_header.vue';
import ImageDiffOverlay from '~/diffs/components/image_diff_overlay.vue';
import { getDiffMode } from '~/diffs/store/utils';
import { diffViewerModes } from '~/ide/constants';
import DiffViewer from '~/vue_shared/components/diff_viewer/diff_viewer.vue';
import { isCollapsed } from '~/diffs/utils/diff_file';
import { FILE_DIFF_POSITION_TYPE, IMAGE_DIFF_POSITION_TYPE } from '~/diffs/constants';
import { useLegacyDiffs } from '~/diffs/stores/legacy_diffs';
import { useNotes } from '~/notes/store/legacy_notes';
const FIRST_CHAR_REGEX = /^(\+|-| )/;
export default {
    name: 'DiffWithNote',
    components: {
        DiffFileHeader,
        GlSkeletonLoader,
        DiffViewer,
        ImageDiffOverlay,
        GlButton
    },
    directives: {
        SafeHtml
    },
    props: {
        discussion: {
            type: Object,
            required: true
        }
    },
    data () {
        return {
            error: false
        };
    },
    computed: {
        ...mapState(useLegacyDiffs, [
            'projectPath'
        ]),
        diffMode () {
            return getDiffMode(this.discussion.diff_file);
        },
        diffViewerMode () {
            return this.discussion.diff_file?.viewer.name;
        },
        fileDiffRefs () {
            return this.discussion.diff_file.diff_refs;
        },
        headSha () {
            return (this.fileDiffRefs ? this.fileDiffRefs.head_sha : this.discussion.commit_id) || '';
        },
        baseSha () {
            return (this.fileDiffRefs ? this.fileDiffRefs.base_sha : this.discussion.commit_id) || '';
        },
        isTextFile () {
            return this.diffViewerMode === diffViewerModes.text;
        },
        hasTruncatedDiffLines () {
            return this.discussion.truncated_diff_lines && this.discussion.truncated_diff_lines.length !== 0;
        },
        isCollapsed () {
            return isCollapsed(this.discussion.diff_file);
        },
        positionType () {
            return this.discussion.position?.position_type;
        },
        isFileDiscussion () {
            if (!this.discussion.diff_file) {
                return this.discussion.original_position.position_type === IMAGE_DIFF_POSITION_TYPE || this.discussion.original_position.position_type === FILE_DIFF_POSITION_TYPE;
            }
            return this.positionType === FILE_DIFF_POSITION_TYPE;
        },
        linkedFileDiscussionPath () {
            const { discussion_path: discussionPath } = this.discussion;
            const file = this.discussion.diff_file?.file_hash;
            return discussionPath && file ? mergeUrlParams({
                file
            }, discussionPath) : discussionPath;
        },
        showHeader () {
            if (this.discussion.diff_file) return true;
            return this.discussion.original_position.position_type === FILE_DIFF_POSITION_TYPE || this.discussion.original_position.position_type === IMAGE_DIFF_POSITION_TYPE;
        },
        backfilledDiffFile () {
            /*
       * https://gitlab.com/gitlab-com/gl-infra/production/-/issues/19118
       *
       * For the vast majority of cases, this should just be discussion.diff_file,
       * but due to that defect, there are potentially some file discussions that cannot
       * be rendered because there is no diff file attached
       *
       * This allows the header to display (sans file mode change) to roughly simulate
       * having an actual diff file.
       */ return this.discussion.diff_file || {
                file_path: this.discussion.original_position.new_path
            };
        }
    },
    mounted () {
        if (this.isTextFile && !this.hasTruncatedDiffLines) {
            this.fetchDiff();
        }
    },
    methods: {
        ...mapActions(useNotes, [
            'fetchDiscussionDiffLines'
        ]),
        fetchDiff () {
            this.error = false;
            this.fetchDiscussionDiffLines(this.discussion).then(this.highlight).catch(()=>{
                this.error = true;
            });
        },
        trimChar (line) {
            return line.replace(FIRST_CHAR_REGEX, '');
        }
    }
};
